import {
  assign,
  Callout,
  CompactPeoplePicker,
  DirectionalHint,
  IBasePicker,
  IBasePickerSuggestionsProps,
  Icon,
  IconButton,
  IPersonaProps,
  Link,
  Persona,
  PersonaSize,
  Spinner,
  SpinnerSize,
  Stack,
} from '@fluentui/react';
import { useOktaAuth } from '@okta/okta-react';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createReferralSource, fetchReferralSource, updateReferralSource } from '../../../api';
import { ROUTES } from '../../../config/routes';
import { isOpsManager } from '../../../okta';
import { fetchAgencies } from '../../../redux/concierge/actions';
import { AgencyIdentity, SearchAgency } from '../../../redux/concierge/types';
import { RootState } from '../../../redux/types';
import useIsMounted from '../../../util/hooks/useIsMounted';
import './ReferralSourcePicker.scss';

enum RequestErrorType {
  fetchAgencies,
  fetchReferralSource,
  saveReferralSource,
}

interface PickerError {
  message?: string;
  type: RequestErrorType;
}

const ReferralSourcePicker: React.FC<{ patientId: string; onChange?: () => void }> = ({ patientId, onChange }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [refSourceAgency, setRefSourceAgency] = useState<SearchAgency>();
  const [showEditing, setShowEditing] = useState(false);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [showToolTip, setShowToolTip] = useState(false);
  const [pickerError, setPickerError] = useState<PickerError>();

  // Edit is only available if referral source is being created for the first time or user is OpsManager
  const [patientHasNoExistingReferralSource, setPatientHasNoExistingReferralSource] = useState(true);
  const { authState } = useOktaAuth();
  const canEditExistingReferralSource = useMemo(() => authState?.accessToken && isOpsManager(authState.accessToken), [authState]);

  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const { agencies, isFetching: agenciesIsFetching } = useSelector((state: RootState) => state.concierge);

  const picker = useRef<IBasePicker<IPersonaProps>>(null);

  const getReferralSource = useCallback(() => {
    setIsLoading(true);
    setPickerError(undefined);
    fetchReferralSource(patientId)
      .then((referralSource) => {
        if (!isMounted.current) {
          return;
        }
        setPatientHasNoExistingReferralSource(!referralSource?.agency);
        setRefSourceAgency(referralSource?.agency);
      })
      .finally(() => isMounted.current && setIsLoading(false));
  }, [patientId, isMounted]);

  useEffect(() => {
    // This check can be removed when [PLAT-4687] is deployed to production
    const agenciesHasNotFinishedFetching = !agencies?.length;
    if (agenciesHasNotFinishedFetching) {
      return;
    }

    // If patientId changes, reset this component's state.
    setIsLoading(false);
    setRefSourceAgency(undefined);
    setShowEditing(false);
    setShowSaveButton(false);
    setShowToolTip(false);
    setPatientHasNoExistingReferralSource(true);
    getReferralSource();
  }, [patientId, agencies, getReferralSource]);

  // When user wants to update the referral source focus the input for them
  useEffect(() => {
    if (showEditing && picker.current) {
      picker.current.focusInput();
    }
  }, [showEditing]);

  // TODO: Include possible error response in RootState.concierge. Refactor usage of concierge to support AsyncData typing.
  // If agencies is empty this is likely an error. Let the user try refetching with a retry button.
  useEffect(() => {
    if (!agenciesIsFetching) {
      const hasAgencies = agencies && agencies.length > 0;
      setPickerError(hasAgencies ? undefined : { message: 'Failed to get agencies.', type: RequestErrorType.fetchAgencies });
    }
  }, [agencies, agenciesIsFetching]);

  function saveReferralSource(patientId: string, agencyId: string, updateExistingReferralSource: boolean) {
    setIsLoading(true);
    setPickerError(undefined);
    (updateExistingReferralSource ? updateReferralSource(patientId, agencyId) : createReferralSource(patientId, agencyId))
      .then((referralSource) => {
        if (!isMounted.current) {
          return;
        }

        setRefSourceAgency(referralSource.agency);
        setPatientHasNoExistingReferralSource(false);
        onChange && onChange();
      })
      .catch((error: Error) => {
        isMounted.current && setPickerError({ message: error?.message, type: RequestErrorType.saveReferralSource });
      })
      .finally(() => {
        if (!isMounted.current) {
          return;
        }
        setIsLoading(false);
        setShowEditing(false);
        setShowSaveButton(false);
      });
  }

  const toolTipCallOut = () =>
    showToolTip ? (
      <Callout directionalHint={DirectionalHint.bottomCenter} target=".helpIconButtonTarget" onDismiss={() => setShowToolTip(false)}>
        <div style={{ margin: 15, width: 300 }}>
          <p>
            Need to change this referral source?
            <br />
            <br />
            Reach out to an Ops Manager and they can edit it here.
          </p>
        </div>
      </Callout>
    ) : null;

  const onFilterChanged = (filterText: string, selectedItems?: IPersonaProps[], limitResults?: number): IPersonaProps[] | Promise<IPersonaProps[]> =>
    filterText ? referralSourcePersonas.filter((item) => item.text?.toLowerCase().startsWith(filterText.toLowerCase())).slice(0, limitResults) : [];

  const referralSourcePersonas: IPersonaProps[] = useMemo(() => {
    const sortOrder = new Map([
      ['Assurance', 0],
      ['e-TeleQuote', 1],
      ['Heal.com', 2],
      ['Healthcare Advantage', 3],
      ['Kindred at Home', 4],
      ['Tranzact', 5],
    ]);
    const suggestedAgencies = agencies?.filter((agency) => agency.flags.includes('REFERRAL_SOURCE')) ?? [];
    const referralSourceAgencies = suggestedAgencies.map((agency) => ({ text: agency.name, imageUrl: agency.logoUrl, ...agency }));
    return referralSourceAgencies.sort((a, b) => {
      const left = sortOrder.get(a.text) ?? Number.MAX_SAFE_INTEGER;
      const right = sortOrder.get(b.text) ?? Number.MAX_SAFE_INTEGER;
      return left - right;
    });
  }, [agencies]);

  const onItemsChange = (items?: IPersonaProps[]) => {
    const selectedAgencyPersona = items?.[0] as SearchAgency;
    if (selectedAgencyPersona) {
      setRefSourceAgency(selectedAgencyPersona);
      setShowEditing(false);
      setShowSaveButton(true);
    }
  };

  const PickerShowingErrorAndRetry = () => {
    let retryCallback: () => void;
    switch (pickerError?.type) {
      case RequestErrorType.fetchAgencies: {
        retryCallback = () => {
          setPickerError(undefined);
          dispatch(fetchAgencies());
        };
        break;
      }
      case RequestErrorType.fetchReferralSource: {
        retryCallback = () => getReferralSource();
        break;
      }
      case RequestErrorType.saveReferralSource: {
        retryCallback = () => refSourceAgency && saveReferralSource(patientId, refSourceAgency?.id, !patientHasNoExistingReferralSource);
        break;
      }
    }

    return (
      <>
        <Icon iconName="Error" style={{ fontSize: 15, marginRight: 5, color: 'red' }} />
        {`Something went wrong. ${pickerError?.message ? pickerError?.message + '.' : ''} Click to retry.`}
        <IconButton iconProps={{ iconName: 'Refresh' }} style={{ marginLeft: 6 }} onClick={() => retryCallback()} />
      </>
    );
  };

  // Display search input box that returns agencies
  const PickerShowingSearchDropdown = () => {
    const limitedSearchAdditionalProps: IBasePickerSuggestionsProps = { resultsMaximumNumber: 6, className: 'agency-selector-suggestion-item' };
    const suggestionProps: IBasePickerSuggestionsProps = { suggestionsHeaderText: 'Suggested Agencies', noResultsFoundText: 'No results found' };

    return (
      <>
        <CompactPeoplePicker
          onResolveSuggestions={onFilterChanged}
          onEmptyInputFocus={() => referralSourcePersonas}
          pickerSuggestionsProps={assign(limitedSearchAdditionalProps, suggestionProps)}
          onChange={onItemsChange}
          resolveDelay={300}
          itemLimit={1}
          componentRef={picker}
          styles={{ root: { width: 300 } }}
        />

        <IconButton iconProps={{ iconName: 'RevToggleKey' }} style={{ marginLeft: 6 }} onClick={() => setShowEditing(false)} />
      </>
    );
  };

  // Once Ops has selected a ReferralSource show a save button to let them confirm the choice.
  const PickerShowingSelectionAndSaveButton: React.FC<{ agency: AgencyIdentity; updateExistingReferralSource: boolean }> = ({
    agency,
    updateExistingReferralSource,
  }: {
    agency: AgencyIdentity;
    updateExistingReferralSource: boolean;
  }) => (
    <>
      <Persona imageUrl={refSourceAgency?.logoUrl} size={PersonaSize.size24} text={agency.name} />
      <IconButton iconProps={{ iconName: 'Save' }} disabled={isLoading} onClick={() => saveReferralSource(patientId, agency.id, updateExistingReferralSource)} />
      <IconButton
        iconProps={{ iconName: 'Cancel' }}
        onClick={() => {
          setShowEditing(true);
          setRefSourceAgency(undefined);
        }}
      />
    </>
  );

  // Default - display referral source or none - Only Ops Managers can change a referral source once it has been selected.
  const PickerShowReferralSource = () => {
    const shouldShowEditButton = canEditExistingReferralSource || patientHasNoExistingReferralSource;
    const linkToReferralSourceAgencyPage = () =>
      refSourceAgency ? (
        <Link href={`${ROUTES.agency}/${refSourceAgency.id}`} target="_blank">
          {refSourceAgency.name}
        </Link>
      ) : null;

    return (
      <>
        {refSourceAgency ? (
          <Persona imageUrl={refSourceAgency?.logoUrl} size={PersonaSize.size24} text={refSourceAgency.name} onRenderPrimaryText={linkToReferralSourceAgencyPage} />
        ) : (
          'None'
        )}
        {shouldShowEditButton ? (
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            onClick={() => {
              setShowEditing(true);
              picker.current && (picker.current as any).focusInput();
            }}
            styles={{ root: { height: 'auto', marginLeft: 6 } }}
          />
        ) : (
          <IconButton
            className="helpIconButtonTarget"
            styles={{ root: { height: 'auto', marginLeft: 6 } }}
            iconProps={{ iconName: 'Info' }}
            onClick={() => setShowToolTip((prevState) => !prevState)}
          />
        )}
        {toolTipCallOut()}
      </>
    );
  };

  return (
    <Stack horizontal verticalAlign="center">
      {(() => {
        if (isLoading || agenciesIsFetching) {
          return <Spinner label={`${isLoading ? 'Processing' : 'Loading agencies'}`} size={SpinnerSize.small} labelPosition="right" />;
        }

        if (pickerError) {
          return <PickerShowingErrorAndRetry />;
        }

        if (showEditing) {
          return <PickerShowingSearchDropdown />;
        }

        if (showSaveButton && refSourceAgency) {
          return <PickerShowingSelectionAndSaveButton agency={refSourceAgency} updateExistingReferralSource={!patientHasNoExistingReferralSource} />;
        }

        return <PickerShowReferralSource />;
      })()}
    </Stack>
  );
};

export default ReferralSourcePicker;
